import { useEffect, useState } from "react";
import { http } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { BookOpen, Trophy, CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ courses: 0, unlocked: 0, attempts: 0, legend: 0 });

  useEffect(() => {
    (async () => {
      const [c, a] = await Promise.all([http.get("/courses"), http.get("/my/attempts").catch(()=>({data:[]}))]);
      setStats({
        courses: c.data.length,
        unlocked: c.data.filter(x => x.unlocked).length,
        attempts: a.data.length,
        legend: a.data.filter(x => x.level === "legend" && x.correct >= x.total - 1).length,
      });
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-headings font-black text-3xl md:text-4xl text-[#1a2332]">Bonjour {user?.name?.split(" ")[0]} 👋</h1>
        <p className="mt-2 text-[#5b6577]">Rôle : <span className="text-[#00a86b] font-semibold">{user?.role}</span>{user?.transmission && user.transmission !== "none" ? ` · Boîte ${user.transmission === "manual" ? "manuelle" : "automatique"}` : ""}</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Stat icon={BookOpen} label="Cours disponibles" value={stats.unlocked} sub={`/ ${stats.courses}`} color="#00a86b" />
        <Stat icon={CheckCircle2} label="Quiz tentés" value={stats.attempts} color="#00a86b" />
        <Stat icon={Trophy} label="Niveau Légende" value={stats.legend} color="#FF8C42" />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <CardLink to="/app/courses" title="Mes cours" desc="Consultez les 36 cours structurés en 4 compétences." bg="#00a86b" testid="cta-courses" icon={BookOpen} />
        <CardLink to="/app/leaderboard" title="Classement Légende" desc="iPhone, Lunettes Meta, AirPods à gagner." bg="#FF8C42" testid="cta-leaderboard" icon={Trophy} />
        {user?.role === "eleve" ? (
          <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm">
            <div className="font-headings font-black text-xl text-[#1a2332]">Règles des quiz</div>
            <div className="text-[#5b6577] text-sm mt-2 leading-relaxed">Niveau 1 : 30s · Niveau 2 : 11s · Légende : 7s. Maximum 1 faute pour débloquer le niveau suivant.</div>
          </div>
        ) : (
          <CardLink to="/app/users" title="Utilisateurs" desc="Gérer élèves, mentors & déblocages." bg="#00a86b" testid="cta-users" icon={ArrowRight} />
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-black/5 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s/g,"-")}`}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-4 font-headings font-black text-3xl text-[#1a2332]">{value}{sub && <span className="text-base text-[#5b6577] font-normal ml-1">{sub}</span>}</div>
      <div className="text-xs text-[#5b6577] mt-1">{label}</div>
    </div>
  );
}

function CardLink({ to, title, desc, bg, testid, icon: Icon }) {
  return (
    <Link to={to} data-testid={testid}
      className="text-white rounded-2xl p-6 hover:-translate-y-1 transition-transform shadow-md"
      style={{ backgroundColor: bg }}>
      <Icon className="w-7 h-7" />
      <div className="mt-4 font-headings font-black text-xl">{title}</div>
      <div className="text-white/85 text-sm mt-1">{desc}</div>
    </Link>
  );
}
